<script setup lang="ts">
import MainBreadcrumbs from "../../../ui/components/header/MainBreadcrumbs.vue";
import TableOverview from "../components/TableOverview.vue";
import modal from "@/ui/components/modal.vue";
import { onBeforeMount, Ref, ref } from "vue";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import router from "@/router/router";
import {
	required,
	minLength,
	maxLength,
	email,
	phoneNumber,
} from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import { useBaseStore } from "@/stores/base";
import Switch from "@/ui/components/switch/switch.vue";

const { t } = useI18n();

const breadcrumbs = [
	{
		title: "Users",
		disabled: true,
		to: "/users",
	},
];

const definedCompanies: Ref<[]> = ref([]);
const newUser: Ref<App.Data.UserDO> = ref();
const availableRoles: Ref<App.Data.RoleDO[]> = ref([]);

const loadNewUser = async () => {
	const [rolesResponse, UserResponse, DefinedCompaniesResponse] =
		await Promise.all([
			useApiClient("roles").getRoles({ per_page: 100 }),
			useApiClient("users").getNewUser(),
			useApiClient("users").getDefinedCompanies(),
		]);

	if (rolesResponse.isOk()) {
		availableRoles.value = rolesResponse.value.data;
	}

	if (UserResponse.isOk()) {
		newUser.value = UserResponse.value;
		newUser.value.roles = [];
	}

	if (DefinedCompaniesResponse.isOk()) {
		definedCompanies.value = DefinedCompaniesResponse.value;
	}
};

onBeforeMount(() => {
	loadNewUser();
});

const closeModal = () => {
	isNewUserVisible.value = false;
	loadNewUser();
};

const submitUser = async () => {
	const response = await toastApiResult(
		useApiClient("users").createUser(newUser.value)
	);
	if (response.isOk()) {
		closeModal();
		router.push({
			path: `/users/edit/${response.value.uuid}`,
		});
	}
};

const isNewUserVisible = ref(false);

function toggleRole(role: App.Data.RoleDO, add: boolean): void {
	const roles = newUser.value.roles;
	add
		? !roles.includes(role) && roles.push(role)
		: roles.splice(roles.indexOf(role), 1);
}

const search = ref("");
</script>

<template>
	<main-breadcrumbs :breadcrumbs="breadcrumbs" />
	<v-card class="pa-5" elevation="4">
		<div class="d-flex flex-nowrap mb-5">
			<v-text-field
				v-model="search"
				class="mr-3"
				prepend-inner-icon="mdi-magnify"
				:label="t('users.overview.search')"
				variant="filled"
				single-line
				hide-details
			/>
			<v-btn
				variant="elevated"
				prepend-icon="fa: fa-light fa-plus"
				height="48"
				@click="isNewUserVisible = true"
			>
				{{ t("users.overview.addUser") }}
			</v-btn>
		</div>

		<modal
			:title="t('users.overview.modal.title')"
			icon="fa-regular fa-plus"
			v-model:visible="isNewUserVisible"
			@close-modal="isNewUserVisible = false"
			@submit="submitUser"
		>
			<div class="d-flex flex-column ga-3">
				<v-text-field
					v-model="newUser.name"
					:label="t('users.overview.modal.inputName')"
					prepend-icon="mdi-account-outline"
					:rules="[required(t), maxLength(t, 255), minLength(t, 3)]"
					clearable
					hide-details="auto"
				/>
				<v-text-field
					v-model="newUser.email"
					:label="t('users.overview.modal.inputEmail')"
					placeholder="your.email@example.com"
					prepend-icon="mdi-at"
					:rules="[required(t), email(t)]"
					clearable
					hide-details="auto"
				/>
				<v-text-field
					v-model="newUser.phone_number"
					:label="t('users.overview.modal.inputPhoneNumber')"
					placeholder="+310612345678"
					prepend-icon="mdi-cellphone"
					:rules="[
						...(useBaseStore().HAS_ENVIRONMENT_FEATURE(
							'user_must_provide_phone_number'
						)
							? [required(t)]
							: []),
						phoneNumber(t),
					]"
					clearable
					hide-details="auto"
				/>
				<v-combobox
					v-model="newUser.company"
					:items="definedCompanies"
					:label="t('users.overview.modal.inputCompany')"
					prepend-icon="mdi-domain"
					:rules="[required(t)]"
					hide-details="auto"
				/>
			</div>

			<v-divider class="mt-5 mb-3" />

			<div v-for="role in availableRoles" :key="role.name_clean">
				<div class="d-flex justify-space-between align-center">
					<div class="d-flex flex-column">
						<span style="font-weight: 500" class="color-on-surface">
							{{ role.name }}
						</span>
						<v-label>
							{{ role.description }}
						</v-label>
					</div>

					<Switch @update:model-value="(value) => toggleRole(role, value)" />
				</div>
			</div>
		</modal>

		<table-overview :search="search" />
	</v-card>
</template>

<style lang="scss">
.card-border-right {
	border-left: 50px solid rgb(var(--v-theme-borderColor));
}
</style>
